import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import api from '../../services/api';
import { getCalcIssues } from '../../services/timesheetService';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import ReportExportButtons from '../../components/ui/ReportExportButtons';
import { buildReportLabels, statusReportLabels, anomalyReportLabels } from '../../utils/reportLabels';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import OptionsMenu from '../../components/ui/OptionsMenu';
import PersonCell from '../../components/ui/PersonCell';
import useMissingRecordsApi from './config';
import usePollCalcJob from '../../hooks/usePollCalcJob';
import { isIsoDay } from '../../utils/attendanceFilterQuery';
import PresetFilterBanner from '../pdks-reports/components/PresetFilterBanner';

// Tek kaydırma: kap esnek sütun ve min-height:0 — afişler sabit yükseklikte tablonun ÜSTÜNDE durur,
// kayan yalnız tablo satırlarıdır (afiş başına tablo yüksekliği aynı ölçüde kısaltılır).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// AKM POC §5.12 — hesaplama sorunu afişi: tek satır, sabit yükseklik; tam metin title'da.
const CALC_ISSUE_BANNER_HEIGHT = 40;
// Devam Paneli süzgeç şeridi (düğmeli tek satır + alt boşluk).
const PRESET_BANNER_HEIGHT = 64;
const CALC_ISSUE_BANNER_GAP = 8;
// Brief: ilk sayfa (5 sorun) yeter — afiş bir uyarıdır, liste değil.
export const CALC_ISSUE_SAMPLE = 5;

const CalcIssueBanner = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: ${CALC_ISSUE_BANNER_HEIGHT}px;
  margin-bottom: ${CALC_ISSUE_BANNER_GAP}px;
  padding: 0 14px;
  border-radius: 6px;
  background: #fdecea;
  border: 1px solid #f5c6cb;
  color: #c0392b;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const BannerText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ENGINE_ERROR = 'engineError';
const INVALID_OVERTIME_POLICY = 'invalidOvertimePolicy';
// İnceleme I2 — kişinin fazla mesai politikası yasal çerçevesiyle (4857 / 657) uyuşmuyor; motor o kişi için
// yasal varsayılan eşikleri kullandı. Satırda userFullName kişi, detail politika adıdır.
const OVERTIME_POLICY_FRAMEWORK_MISMATCH = 'overtimePolicyFrameworkMismatch';

// GET /v1/timesheets/calc-issues?code=… yanıtlarından afiş içeriği. Her kod AYRI çağrılır; totalRowCount o kodun
// KESİN sayısıdır. engineError ve overtimePolicyFrameworkMismatch satırları kişi adlarını verir (kişi başına tek
// kayıt); sayfaya sığmayanlar varsa ad listesi "…" ile biter. invalidOvertimePolicy satırlarında Detail politikanın
// adıdır.
const pageOf = (result) => {
  const rows = Array.isArray(result) ? result : (result?.data || []);
  const total = Array.isArray(result) ? rows.length : (result?.totalRowCount ?? rows.length);
  return { rows, total, hidden: Math.max(0, total - rows.length) };
};

const joinNames = (names, hidden) => (hidden > 0 && names.length > 0 ? `${names.join(', ')}, …` : names.join(', '));

const personNames = (page) => [...new Set(page.rows.map((r) => r.userFullName || '').filter(Boolean))];

export const summarizeCalcIssues = (engineResult, policyResult, mismatchResult) => {
  const engine = pageOf(engineResult);
  const policy = pageOf(policyResult);
  const mismatch = pageOf(mismatchResult);
  const policyNames = [...new Set(policy.rows.map((r) => r.detail || '').filter(Boolean))];
  return {
    engineCount: engine.total,
    engineNames: joinNames(personNames(engine), engine.hidden),
    policyNames: joinNames(policyNames, policy.hidden),
    mismatchCount: mismatch.total,
    mismatchNames: joinNames(personNames(mismatch), mismatch.hidden),
  };
};

const EMPTY_ISSUES = { engineCount: 0, engineNames: '', policyNames: '', mismatchCount: 0, mismatchNames: '' };

// DataTable üstünde satır içi hesap durumu (bileşen toast'ı YOK — kural; mesaj sayfada gösterilir).
// $error yalnız GERÇEK terminal başarısızlıkta; recalculating/timeout nötr stildedir.
const RecalcNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $error }) => ($error ? '#fdecea' : '#eef6fd')};
  color: ${({ $error }) => ($error ? '#c0392b' : '#0270BA')};
  border: 1px solid ${({ $error }) => ($error ? '#f5c6cb' : '#cfe6fb')};
`;

// Satırın gününde saat 17:00 (yerel) — manuel puls için varsayılan zaman damgası. DateOnly "yyyy-MM-dd"
// yerel tarih olarak kurulur (gün kayması olmadan), ISO string olarak forma verilir; datetime picker
// bunu yerel 17:00 gösterir, dokunulmazsa aynen POST edilir (MovementRecordsPage manuel akışıyla tutarlı).
const defaultTimestampFor = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).substring(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 17, 0, 0).toISOString();
};

const currentMonthStart = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`;
};

// Devam Paneli'nin "eksik çıkış" uyarısı bu sayfayı uyarının saydığı aralık ve anomali koduyla açar
// (?from=yyyy-MM-dd&to=yyyy-MM-dd&code=missing_exit — inceleme I4). Geçersiz değerler yok sayılır.
export function readMissingRecordsPreset(search) {
  const params = new URLSearchParams(search || '');
  const from = params.get('from');
  const to = params.get('to');
  const code = params.get('code');
  const range = isIsoDay(from) && isIsoDay(to) && from <= to ? { from, to } : null;
  const validCode = code && /^[a-z_]{1,40}$/.test(code) ? code : null;
  if (!range && !validCode) return null;
  return { from: range?.from || null, to: range?.to || null, code: validCode };
}

// Sayfanın varsayılan istek parametreleri: preset varsa onun aralığı/kodu, yoksa içinde bulunulan ay.
export function missingRecordsDefaults(preset, monthStart) {
  const params = { pageSize: 10, pageNumber: 1 };
  if (preset?.from && preset?.to) params['date[between]'] = `${preset.from},${preset.to}`;
  else params['date[gte]'] = monthStart;
  if (preset?.code) params['code[eq]'] = preset.code;
  return params;
}

export default function MissingRecordsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  // Varsayılan filtre: içinde bulunulan ay (date[gte] = ay başı) — queryParams başlangıcı. Devam Paneli'nden gelindiyse
  // adresteki aralık ve kod (yapışkan: tablo değişimlerinde korunur; şeritteki Süzgeci Kaldır siler).
  const monthStart = useMemo(() => currentMonthStart(), []);
  const location = useLocation();
  const history = useHistory();
  const preset = useMemo(() => readMissingRecordsPreset(location.search), [location.search]);
  const presetKey = preset ? JSON.stringify(preset) : '';
  const presetRef = useRef(preset);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState(() => missingRecordsDefaults(preset, monthStart));

  useEffect(() => {
    const previous = presetRef.current;
    presetRef.current = preset;
    if ((previous ? JSON.stringify(previous) : '') === presetKey) return;
    setQueryParams(missingRecordsDefaults(preset, monthStart));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey]);

  const clearPreset = useCallback(() => {
    history.replace({ pathname: location.pathname, search: '' });
  }, [history, location.pathname]);
  const [filterList, setFilterList] = useState([]);
  const [gates, setGates] = useState([]);

  const {
    columns: baseColumns, data, fetching, pagination, refresh, statusLabels,
  } = useMissingRecordsApi(queryParams);
  const {
    calculating, calcFailed, calcErrorKey, calcTimedOut, resetCalcState, runCalculate,
  } = usePollCalcJob();

  // Hesaplama sorunları afişi (AKM POC §5.12). api.js GET'te REDDETMEZ: uç başarısızsa (status !== true)
  // afiş gösterilmez — eski bir afiş de kalmaz.
  const [calcIssues, setCalcIssues] = useState(EMPTY_ISSUES);
  const [issuesTrigger, setIssuesTrigger] = useState(0);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getCalcIssues({ pageNumber: 1, pageSize: CALC_ISSUE_SAMPLE, code: ENGINE_ERROR }),
      getCalcIssues({ pageNumber: 1, pageSize: CALC_ISSUE_SAMPLE, code: INVALID_OVERTIME_POLICY }),
      getCalcIssues({ pageNumber: 1, pageSize: CALC_ISSUE_SAMPLE, code: OVERTIME_POLICY_FRAMEWORK_MISMATCH }),
    ]).then(([engineRes, policyRes, mismatchRes]) => {
      if (cancelled) return;
      const ok = (res) => res?.data?.status === true;
      setCalcIssues(summarizeCalcIssues(
        ok(engineRes) ? engineRes.data.data : [],
        ok(policyRes) ? policyRes.data.data : [],
        ok(mismatchRes) ? mismatchRes.data.data : [],
      ));
    }).catch(() => {
      if (!cancelled) setCalcIssues(EMPTY_ISSUES);
    });
    return () => { cancelled = true; };
  }, [issuesTrigger]);

  const engineBanner = calcIssues.engineCount > 0
    ? t('missingRecords.calcIssuesBanner', { count: calcIssues.engineCount, names: calcIssues.engineNames })
    : null;
  const policyBanner = calcIssues.policyNames
    ? t('missingRecords.invalidOvertimePolicyBanner', { name: calcIssues.policyNames })
    : null;
  const mismatchBanner = calcIssues.mismatchCount > 0
    ? t('missingRecords.frameworkMismatchBanner', { count: calcIssues.mismatchCount, names: calcIssues.mismatchNames })
    : null;
  // Tek kaydırma: her afiş sabit yükseklikte; tablo gövdesi görünen afiş sayısı (0-3) kadar kısalır.
  const bannerCount = [engineBanner, policyBanner, mismatchBanner].filter(Boolean).length;
  // Devam Paneli şeridi (yalnız panelin aralığı GERÇEKTEN uygulanırken görünür — PresetFilterBanner ile aynı koşul).
  const presetShown = Boolean(preset?.from && preset?.to && queryParams['date[between]'] === `${preset.from},${preset.to}`);
  const extraHeight = bannerCount * (CALC_ISSUE_BANNER_HEIGHT + CALC_ISSUE_BANNER_GAP) + (presetShown ? PRESET_BANNER_HEIGHT : 0);
  const tableBodyMaxHeight = extraHeight > 0
    ? `calc(100vh - 280px - ${extraHeight}px)`
    : undefined;

  // Manuel kayıt drawer'ı yalnız AC.Write ile (uç AC.Write ister — TA yetkisi YETMEZ). Kapı listesi
  // yalnız yazma yetkilisi için çekilir (drawer select'ini besler).
  const canWriteAc = canWrite('AC');
  useEffect(() => {
    if (!canWriteAc) return;
    api.get('/v1/gates?pageSize=500').then((res) => {
      setGates(res.data?.data?.data || res.data?.data || []);
    }).catch(() => {});
  }, [canWriteAc]);

  const handleAddManual = useCallback((row) => {
    if (calculating) return; // poll sürerken aksiyon disabled
    resetCalcState();
    setSelectedRow(row);
    setDrawerOpen(true);
  }, [calculating, resetCalcState]);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedRow(null);
  }, []);

  // Kayıt sonrası otomatik yeniden hesap: manuel puls persist edilince o gün + o kullanıcı için
  // calculate kuyruğa yazılır; succeeded → liste yenilenir (düzeltilen gün listeden düşer).
  const handleSave = useCallback(async (formData) => {
    if (!selectedRow) return false;
    try {
      await api.post('/v1/movement-records/manual', formData);
    } catch {
      return false; // interceptor toast bastı; drawer açık kalır
    }
    const day = String(selectedRow.date).substring(0, 10);
    runCalculate(
      { startDate: day, endDate: day, userIds: [selectedRow.userId] },
      // Yeniden hesap bir hesaplama sorununu kapatmış olabilir — afiş de tazelenir.
      { onSucceeded: () => { refresh(); setIssuesTrigger((p) => p + 1); } },
    );
    return true; // drawer kapanır; RecalcNotice akışı devralır
  }, [selectedRow, runCalculate, refresh]);

  const searchUsers = useCallback(async (query) => {
    const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const result = res.data?.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    return items.map((u) => ({
      id: u.userId || u.id,
      name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
      profilePicture: u.profilePicture,
    }));
  }, []);

  const directionOptions = useMemo(() => [
    { id: 'entry', name: t('gate.entry') },
    { id: 'exit', name: t('gate.exit') },
  ], [t]);

  // Ön dolu başlangıç objesi (selectedRow'u ham geçmek yerine ŞEMAYA uygun kur): userId satırdan,
  // timestamp satırın günü + 17:00. gateId/direction/note kullanıcı doldurur (note zorunlu — gerekçe).
  const initialValues = useMemo(() => {
    if (!selectedRow) return null;
    return {
      userId: selectedRow.userId,
      timestamp: defaultTimestampFor(selectedRow.date),
    };
  }, [selectedRow]);

  const objectSchema = useMemo(() => [
    {
      name: 'userId', label: t('movementRecord.userFullName'), type: 'asyncSelect', grid: 12, required: true,
      onSearch: searchUsers,
      // Ön dolu userId'nin etiketini API'siz çöz — satırın bilinen ad/foto'su kullanılır.
      onGetById: async (id) => ({ id, name: selectedRow?.userFullName || '', profilePicture: selectedRow?.userProfilePicture }),
      renderOption: (o) => <PersonCell name={o.name} profilePicture={o.profilePicture} />,
    },
    {
      name: 'gateId', label: t('movementRecord.gateName'), type: 'select', grid: 12, required: true,
      options: gates, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'direction', label: t('movementRecord.direction'), type: 'select', grid: 12, required: true,
      options: directionOptions, idKey: 'id', labelKey: 'name',
    },
    { name: 'timestamp', label: t('movementRecord.timestamp'), type: 'datetime', grid: 12, required: true },
    { name: 'note', label: t('movementRecord.note'), type: 'textarea', grid: 12, required: true },
  ], [t, gates, directionOptions, searchUsers, selectedRow]);

  const actionOptions = useMemo(() => {
    if (!canWriteAc) return [];
    return [
      { type: 'add', label: t('missingRecords.addManualRecord'), icon: Plus, color: '#0291EF', onClick: handleAddManual },
    ];
  }, [canWriteAc, t, handleAddManual]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    // Kullanıcı kendi tarih filtresini uygulamadıysa ay-başı alt sınırı (ya da panelden gelen aralık) korunur
    // (sayfalama/sıralama tüm geçmişe açılmasın). Kendi date filtresini girerse (gte/lte/between) varsayılan eklenmez.
    const active = presetRef.current;
    if (!params['date[gte]'] && !params['date[lte]'] && !params['date[between]']) {
      if (active?.from && active?.to) params['date[between]'] = `${active.from},${active.to}`;
      else params['date[gte]'] = monthStart;
    }
    if (active?.code && !params['code[eq]']) params['code[eq]'] = active.code;
    setQueryParams(params);
  }, [columns, monthStart]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // AKM POC §7.1 — sunucu tarafı Excel / PDF: o anki süzgeçler (panel aralığı ve anomali kodu dahil — queryParams zaten
  // taşır), TAM veri. Yerleşik "yalnız ekrandaki sayfa" CSV'si kapalı.
  const getExportLabels = useCallback(() => buildReportLabels(t, baseColumns, t('missingRecords.title'), {
    ...statusReportLabels(statusLabels),
    ...anomalyReportLabels(t),
  }), [t, baseColumns, statusLabels]);

  const toolbarActions = useMemo(() => (
    <ReportExportButtons url="/v1/timesheets/anomalies/export" params={queryParams} getLabels={getExportLabels} />
  ), [queryParams, getExportLabels]);

  return (
    <Container>

      <GenericFormDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        title={t('missingRecords.addManualRecord')}
        schema={objectSchema}
        initialValues={initialValues}
        onSave={handleSave}
      />

      {(calculating || calcFailed || calcTimedOut) && (
        <RecalcNotice $error={calcFailed}>
          {calculating && t('missingRecords.recalculating')}
          {calcFailed && (
            <span>
              {t('timesheet.calculateFailedInline')}
              {calcErrorKey && ` — ${t(`responseErrors.${calcErrorKey}`, { defaultValue: calcErrorKey })}`}
            </span>
          )}
          {calcTimedOut && t('timesheet.calculateStillRunning')}
        </RecalcNotice>
      )}

      <PresetFilterBanner preset={preset} queryParams={queryParams} onClear={clearPreset} />

      {engineBanner && (
        <CalcIssueBanner role="alert" data-testid="calc-issues-banner" title={engineBanner}>
          <BannerText>{engineBanner}</BannerText>
        </CalcIssueBanner>
      )}
      {policyBanner && (
        <CalcIssueBanner role="alert" data-testid="invalid-overtime-policy-banner" title={policyBanner}>
          <BannerText>{policyBanner}</BannerText>
        </CalcIssueBanner>
      )}
      {mismatchBanner && (
        <CalcIssueBanner role="alert" data-testid="framework-mismatch-banner" title={mismatchBanner}>
          <BannerText>{mismatchBanner}</BannerText>
        </CalcIssueBanner>
      )}

      <DataTable
        title={t('missingRecords.title')}
        tableBodyMaxHeight={tableBodyMaxHeight}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        toolbarActions={toolbarActions}
        download={false}
      />
    </Container>
  );
}
